/**
 * Curated tutorial thumbnail manifest for the Learn tab.
 *
 * Why: legacy seed data shipped only 2 unsplash IDs for 20+ lessons, so the
 * Learn page felt repetitive and off-theme. This manifest maps each lesson
 * category to a small rotation of authentic UK carp-fishing tutorial-style
 * photos (rig close-ups, bait spreads, rods on pods, casting, fish-care,
 * misty bivvy lake scenes etc.) and picks a deterministic image per lesson
 * so the same tutorial always shows the same thumb.
 *
 * Authors can override the auto-pick by setting `thumbnail_url` on the
 * tutorial document (admin upload pipeline). Otherwise the resolver auto-
 * picks from this curated set.
 */

const UNSPLASH_PARAMS = 'auto=format&fit=crop&q=80&w=800';
const u = (id) => `https://images.unsplash.com/${id}?${UNSPLASH_PARAMS}`;

/**
 * Category rotations. All photos vetted as carp-fishing or directly-related
 * tutorial subjects.
 */
const ROTATIONS = {
  // Knots — close-ups of line / hook tying / hands working terminal tackle.
  knots: [
    u('photo-1545450660-3378a7f3a364'),    // anglers tackle on a still lake
    u('photo-1604227694902-8d79311c6f19'), // misty dawn lake (atmospheric)
    u('photo-1761393296074-4baf280c6406'), // angler holding carp / hands
    u('photo-1738597602799-62422e1eeb8a'), // sunrise lake silhouette
  ],
  // Rigs — terminal tackle / hooked baits / pre-tied setups.
  rigs: [
    u('photo-1761393296074-4baf280c6406'), // carp with hook / hands close
    u('photo-1545450660-3378a7f3a364'),    // tackle box scene
    u('photo-1532724414795-8a873d116fab'), // tree-lined lake (setup atmosphere)
    u('photo-1758272960374-343f5eb7fdcd'), // bivvy / lakeside fire (session prep)
  ],
  // Setups — full rods-on-pod, bivvy, bank-side gear shots.
  setups: [
    u('photo-1758272960374-343f5eb7fdcd'), // lakeside bivvy / campfire setup
    u('photo-1604227694902-8d79311c6f19'), // misty dawn lake
    u('photo-1532724414795-8a873d116fab'), // tree-lined calm water
    u('photo-1738597602799-62422e1eeb8a'), // sunrise rod silhouette mood
  ],
  // Fallback (any uncategorised lesson)
  default: [
    u('photo-1604227694902-8d79311c6f19'),
    u('photo-1738597602799-62422e1eeb8a'),
    u('photo-1545450660-3378a7f3a364'),
    u('photo-1532724414795-8a873d116fab'),
    u('photo-1761393296074-4baf280c6406'),
    u('photo-1758272960374-343f5eb7fdcd'),
  ],
};

export const TUTORIAL_FALLBACK = ROTATIONS.default[0];

// Small djb2 — deterministic across renders so the same lesson keeps the same image
const djb2 = (s) => {
  s = String(s || '');
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

/** Pick a tutorial thumbnail deterministically for a category/seed pair. */
export const pickTutorialThumb = (category, seed) => {
  const rot = ROTATIONS[category] || ROTATIONS.default;
  const idx = djb2(seed || category || '') % rot.length;
  return rot[idx];
};

/**
 * Resolve a tutorial's final thumbnail URL:
 * 1) honour `thumbnail_url` / `thumbnail` if it's a real http(s) URL — that's
 *    the admin upload override.
 * 2) otherwise auto-pick from the curated rotation for that category.
 */
export const resolveTutorialThumb = (lesson) => {
  const direct = lesson && (lesson.thumbnail_url || lesson.thumbnail);
  if (direct && typeof direct === 'string' && direct.startsWith('http')) {
    return direct;
  }
  const seed = (lesson && (lesson.session_id || lesson.id || lesson.title)) || '';
  return pickTutorialThumb(lesson && lesson.category, seed);
};
